import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

// Primitivos de formulario del sistema de diseño.
//
// Nacen aquí, en el rediseño del formulario de producto (C-2), y son lo
// que C-3 reparte por el resto de las pantallas. Hasta ahora cada
// formulario resolvía lo mismo a su manera: la validación como un banner
// rojo al final, los campos apilados en una columna de 448 px, y los
// catálogos como un `<input>` de texto libre.

// Una sección con su porqué. El subtítulo NO es decoración: los tres
// campos que más confunden del catálogo (flujo, estacionalidad, familia)
// se volvieron incomprensibles por estar sueltos junto al nombre, sin
// nada que dijera para qué sirven.
export function Seccion({ titulo, descripcion, children, columnas = 2 }) {
  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{titulo}</h4>
        {descripcion && (
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{descripcion}</p>
        )}
      </div>
      <div className={`grid gap-3 ${columnas === 2 ? 'sm:grid-cols-2' : ''}`}>
        {children}
      </div>
    </section>
  );
}

// Campo con validación EN LÍNEA y al salir del campo, no un banner al
// final. Un error que aparece cuando ya pulsaste "Guardar" te obliga a
// releer once campos para encontrar cuál era.
//
// `validar` recibe el valor y devuelve un mensaje en español o null.
export function CampoValidado({
  etiqueta, ayuda, requerido, valor, validar, children, ancho,
}) {
  const [tocado, setTocado] = useState(false);
  const mensaje = tocado && validar ? validar(valor) : null;

  const control = children({
    onBlur: () => setTocado(true),
    'aria-invalid': mensaje ? 'true' : undefined,
    className: `entrada ${mensaje ? 'border-red-400 focus:border-red-500' : ''}`,
  });

  return (
    <label className={`block ${ancho === 'completo' ? 'sm:col-span-2' : ''}`}>
      <span className="block text-sm font-semibold text-gray-700 mb-1">
        {etiqueta}
        {requerido && <span className="text-red-600 ml-0.5" aria-hidden="true">*</span>}
      </span>
      {control}
      {mensaje ? (
        <span role="alert" className="block text-xs text-red-700 mt-1">{mensaje}</span>
      ) : (
        ayuda && <span className="block text-xs text-gray-500 mt-1">{ayuda}</span>
      )}
    </label>
  );
}

// Selector de catálogo que acepta escribir uno nuevo.
//
// La alternativa —obligar a salirse de la pantalla a crear la categoría
// antes de poder dar de alta el producto— es exactamente la fricción que
// produjo el desorden que el catálogo viene a arreglar: quien tiene 300
// SKUs que capturar escribe cualquier cosa con tal de no salirse.
//
// opciones: [{ id, nombre }] · valor: { id, nombre }
export function Autocompletar({
  opciones, valor, onChange, placeholder = 'Escribe o elige…', permitirNuevo = true,
}) {
  const [texto, setTexto] = useState(valor?.nombre || '');
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);

  // Cuando el valor cambia DESDE FUERA, el texto visible lo sigue. Se
  // ajusta durante el render y no en un efecto: es el patrón que React
  // recomienda para esto, y un efecto encadenaría un render de más cada
  // vez que el padre reasigna el mismo valor.
  const [ultimo, setUltimo] = useState(valor?.nombre || '');
  if ((valor?.nombre || '') !== ultimo) {
    setUltimo(valor?.nombre || '');
    setTexto(valor?.nombre || '');
  }

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const norm = (s) => (s || '').trim().toUpperCase();
  const filtradas = useMemo(
    () => opciones.filter((o) => norm(o.nombre).includes(norm(texto))).slice(0, 8),
    [opciones, texto],
  );
  // Coincidencia exacta ignorando mayúsculas y espacios: es la misma
  // regla que impone el índice único de la base de datos, así que la
  // pantalla no puede ofrecer "crear" algo que el servidor va a fusionar.
  const yaExiste = opciones.some((o) => norm(o.nombre) === norm(texto));
  const puedeCrear = permitirNuevo && norm(texto).length >= 2 && !yaExiste;

  function elegir(o) {
    onChange(o);
    setTexto(o?.nombre || '');
    setAbierto(false);
  }

  return (
    <div className="relative" ref={caja}>
      <div className="relative">
        <input
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          onBlur={() => {
            // Al salir sin elegir nada de la lista, lo escrito vale como
            // valor nuevo: el servidor lo resuelve contra el catálogo.
            if (!norm(texto)) onChange(null);
            else if (!yaExiste && permitirNuevo) onChange({ id: null, nombre: texto.trim() });
          }}
          placeholder={placeholder}
          className="entrada pr-8"
        />
        <ChevronDown className="h-4 w-4 text-gray-500 absolute right-2.5 top-3 pointer-events-none" />
      </div>

      {abierto && (filtradas.length > 0 || puedeCrear) && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg
                        shadow-lg max-h-56 overflow-y-auto">
          {filtradas.map((o) => (
            <button
              key={o.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => elegir(o)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              {norm(o.nombre) === norm(valor?.nombre)
                ? <Check className="h-3.5 w-3.5 text-[var(--color-marca)]" />
                : <span className="w-3.5" />}
              {o.nombre}
            </button>
          ))}
          {puedeCrear && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => elegir({ id: null, nombre: texto.trim() })}
              className="w-full text-left px-3 py-1.5 text-sm text-[var(--color-marca)] font-semibold
                         hover:bg-gray-50 border-t border-gray-100"
            >
              + Crear «{texto.trim()}»
            </button>
          )}
        </div>
      )}
    </div>
  );
}
